import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getTrackingLists } from '../../../redux/actions/trackingList/trackingListAction';
import { updateTrackingList } from '../../../redux/reducers/trackingListSlice';
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from 'react-router-dom';
import { apiConstants, getGeneric } from "../../../common/constants";
import { Button, Typography, Box, Paper, Divider, Alert } from '@mui/material';
import { DownloadRounded, UploadFileRounded, SearchRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import axios from "axios";
import { useSnackbar } from 'notistack';

const MAX_AWB_COUNT = 20;

const TrackBox = () => {
    const { register, handleSubmit, formState: { errors } } = useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const inputFile = useRef(null);
    const [loading, setLoading] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [awbLimitError, setAwbLimitError] = useState('');

    const onButtonClick = () => {
        inputFile.current.click();
    };

    const handleUpload = (e) => {
        setBulkLoading(true);
        dispatch(getTrackingLists(e.target.files[0])).then(() => {
            setBulkLoading(false);
            enqueueSnackbar('Tracking data loaded successfully', { variant: 'success' });
            if (location.pathname !== '/tracking-list') navigate('/tracking-list');
            if (inputFile.current) {
                inputFile.current.value = null;
            }
        }).catch((err) => {
            console.log(err);
            setBulkLoading(false);
            enqueueSnackbar('Some error occurred', { variant: 'error' });
            if (inputFile.current) {
                inputFile.current.value = null;
            }
        });
    };

    const onSubmit = async (data) => {
        let config = { headers: { "Content-Type": "application/json" } };
        let awb = (data.awb).replace(/\s/g, '');
        const awbList = awb.split(',').filter(a => a.trim() !== '');

        if (awbList.length > MAX_AWB_COUNT) {
            setAwbLimitError(`You have entered ${awbList.length} AWBs. Maximum ${MAX_AWB_COUNT} AWBs are allowed. For bulk tracking (up to 5000 AWBs), please use the Upload option below.`);
            return;
        }

        setAwbLimitError('');
        setLoading(true);
        try {
            const url = `https://plccqrpm5g3nal4k752kggoqvq0wcyfc.lambda-url.ap-south-1.on.aws/?waybills=${(awb)}`;
            const response = await axios.get(url, config);
            if (response?.status === 200) {
                const data = response?.data;
                const payload = Array.isArray(data)
                    ? { Status: 200, waybillDetails: data }
                    : data;
                dispatch(updateTrackingList(payload));
                if (location.pathname !== '/tracking-list') navigate('/tracking-list');
            }
        } catch (error) {
            console.error('Error fetching tracking data:', error);
            enqueueSnackbar('Error fetching tracking data', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleBulkSample = () => {
        getGeneric(
            `${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=TRACK_ORDER`
        )
            .then((response) => {
                const binaryData = [];
                binaryData.push(response.data);
                const href = URL.createObjectURL(
                    new Blob(binaryData, { type: "application/octet-stream" })
                );
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", "TRACK_ORDER.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    return (
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Box>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, position: 'relative' }}>
                            <input
                                {...register('awb', {
                                    required: {
                                        value: true,
                                        message: "Please enter at least one AWB number"
                                    },
                                    onChange: () => { setAwbLimitError(''); }
                                })}
                                className="form-control"
                                style={{
                                    borderRadius: '12px',
                                    padding: '14px 20px',
                                    border: `1px solid ${errors.awb || awbLimitError ? '#d32f2f' : '#c4c4c4'}`,
                                    width: '100%',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                }}
                                placeholder="Enter AWB numbers separated by commas (max 20)"
                                aria-label="Track Orders"
                                aria-describedby="button-track-orders"
                            />
                        </Box>
                        <LoadingButton
                            loading={loading}
                            type="submit"
                            id="button-track-orders"
                            variant="contained"
                            disableElevation
                            startIcon={<SearchRounded />}
                            sx={{
                                borderRadius: '12px',
                                padding: '12px 32px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '15px',
                                whiteSpace: 'nowrap',
                                minWidth: 'fit-content',
                            }}
                        >
                            Track Order
                        </LoadingButton>
                    </Box>

                    {errors.awb && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                            {errors.awb.message}
                        </Typography>
                    )}
                    {awbLimitError && (
                        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '8px' }}>
                            {awbLimitError}
                        </Alert>
                    )}
                </form>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        For bulk tracking (up to 5,000 AWBs), upload a file:
                    </Typography>
                    <input type='file' id='file' accept='.csv,.xlsx,.xls' onChange={handleUpload} ref={inputFile} style={{ display: 'none' }} />
                    <LoadingButton
                        startIcon={<UploadFileRounded />}
                        type="button"
                        onClick={onButtonClick}
                        loading={bulkLoading}
                        variant="outlined"
                        size="small"
                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                        Upload
                    </LoadingButton>
                    <Button
                        startIcon={<DownloadRounded />}
                        type="button"
                        onClick={handleBulkSample}
                        variant="text"
                        size="small"
                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                        Sample
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};
export default TrackBox;